import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { equal } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { MaterialType, PosterCategory } from '@/client/courses';
import { FilterNamesEnum } from '@/client/helpers';
import { HubspotProperty } from '@/client/hubspot/types';
import { Actions, Subjects } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { GridTable } from '@/components/grid-table';
import {
  DataTableFilters,
  DataTableToolbar,
  FilterType,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useCourses } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { PosterCard } from '@/ui/poster-card';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import { postersColumnsWidth } from '@/utils/helpers';
import { hubspotTimestamp, hubspotTrack } from '@/utils/hubspot';
import { debounce } from 'lodash';
import { InputText } from 'primereact/inputtext';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledCardGridContainer = styled(CardGridContainer)`
  width: 100%;
  grid-template-columns: repeat(4, 1fr);
  margin-top: 0;

  @media screen and (max-width: ${postersColumnsWidth.THREE}px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media screen and (max-width: ${postersColumnsWidth.TWO}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${postersColumnsWidth.ONE}px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export const PostersPage = () => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const navigate = useNavigate();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 300),
    [],
  );

  const {
    skip,
    take,
    apiFilters,
    onGridPageChange,
    onGridRowChange,
    setSkip,
    onFilter,
  } = useTable({ tableName: TableNamesEnum.POSTERS_LIST });

  const { isLoading, courses: posters } = useCourses({
    accountId: currentAccount?.isSystem
      ? undefined
      : (currentAccount?.id as string),
    take: take,
    skip: skip,
    search: multiSearchValue,
    filters: [...apiFilters, equal('type', MaterialType.POSTER)],
    staleTime: Infinity,
  });

  useEffect(() => {
    hubspotTrack(HubspotProperty.POSTERS_VIEWED, hubspotTimestamp());
  }, []);

  const pathItems = [
    {
      label: currentAccount?.name,
      url: !currentAccount?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](currentAccount?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](currentAccount?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('posters'),
      url: RedirectPaths[RedirectPathsEnum.POSTERS](),
      template: AppBreadCrumbTemplate,
    },
  ];

  const filters = (t: TranslationFunctionType): FilterType[] => [
    {
      label: t('filter.category'),
      field: FilterNamesEnum.POSTER_CATEGORIES,
      placeholder: t('filter.category'),
      type: FilterTypeEnum.MULTI_SELECT,
      options: Object.values(PosterCategory).map((category) => ({
        label: t(`posters.${category}`),
        value: category,
      })),
    },
    {
      label: t('generic.lang'),
      field: FilterNamesEnum.COURSE_LANGUAGES,
      placeholder: t('generic.lang'),
      type: FilterTypeEnum.MULTI_SELECT,
      options: Object.values(LanguagesEnum).map((language) => ({
        label: languageDisplayLabel[language],
        value: language,
      })),
    },
  ];

  const toolbar = (
    <DataTableToolbar justify="space-between">
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        {!!filters(t).length && (
          <DataTableFilters
            filters={filters(t)}
            onFilter={onFilter}
            tableName={TableNamesEnum.POSTERS_LIST}
            defaultValues={{
              posterCategories: [],
              languages: [],
            }}
            className="flex-initial"
          />
        )}
        <div className="p-input-icon-left flex-auto min-w-300">
          <InputText
            className="w-full"
            onInput={debouncedSetMultiSearchValue}
            placeholder={t('generic.name.search')}
            autoComplete="off"
          />
          <i className="pi pi-search" />
        </div>
      </FlexContainer>
    </DataTableToolbar>
  );

  return (
    <>
      <AppBreadCrumb model={pathItems} />
      <FlexContainer justify="space-between" className="mb-4">
        <h1 className="mb-0">{t('posters')}</h1>
        {can(Actions.CREATE, Subjects.COURSES) && currentAccount?.isSystem && (
          <AppButton
            label={t('button.createNew')}
            severity="secondary"
            onClick={() => {
              navigate(RedirectPaths[RedirectPathsEnum.POSTER_CREATE]());
            }}
          />
        )}
      </FlexContainer>
      <GridTable
        isLoading={isLoading}
        count={posters?.count}
        take={take}
        skip={skip}
        onPageChange={onGridPageChange}
        onRowsChange={onGridRowChange}
        toolbarContent={toolbar}
        rowsPerPage={[12, 24, 48]}
      >
        <FlexContainer direction="column" align="flex-start">
          {!!posters?.count && (
            <StyledCardGridContainer>
              {posters?.result.map((poster) => (
                <PosterCard key={poster.id} poster={poster} withActions />
              ))}
            </StyledCardGridContainer>
          )}
        </FlexContainer>
      </GridTable>
    </>
  );
};
